/**
 * PostRaceImmediateBanner — v16.5.X T1 4b sub-paquete X (Alberto 19/06/26).
 *
 * Banner inmediato post-race (race+1) que se renderiza ENCIMA de la
 * recovery week activa. NO modal bloqueante — el atleta puede ver
 * la recovery week debajo. 2 botones:
 *   1. "Empezar plan nuevo" → navega a /plan/crear (CreatePlanPage).
 *   2. "Mantenimiento" → invoca `onMaintenanceClick` (el padre POST a
 *      `/training-plans` plan_type='maintenance' y refresca el plan).
 *
 * Dismissable con X en la esquina superior derecha. El dismiss llama
 * al backend (persistente con re-aparición 24h + expiración 30d) y
 * actualiza optimistically el estado local del padre.
 *
 * Sub-paquete Y v16.5.AB (Y.C.1): se eliminó el badge "Próximamente" del
 * botón "Mantenimiento" — ya está conectado al endpoint real.
 *
 * Diferencia con `PostRecoveryOptions` (existente):
 *   - PostRecoveryOptions: race+8 días (recovery week TAMBIÉN completada),
 *     ocupa toda la pantalla, NO hay recovery activa.
 *   - PostRaceImmediateBanner (este): race+1 día (recovery week ACTIVA),
 *     se solapa por encima del plan, atleta puede ver ambos.
 */
import { useNavigate } from "react-router-dom";

import { t } from "@/i18n/es";

export interface PostRaceImmediateBannerProps {
  /** Callback para cerrar el banner. El padre debe (a) llamar al
   *  backend (persistente) y (b) ocultar el banner localmente. */
  onDismiss: () => void;
  /** Callback al click en "Mantenimiento". Sub-paquete Y v16.5.AB (Y.C.1):
   *  el padre llama `createMaintenancePlan()`, muestra toast contextual
   *  según `cycle_number`/`was_reset` y refresca el plan activo. */
  onMaintenanceClick: () => void;
}

export function PostRaceImmediateBanner({
  onDismiss,
  onMaintenanceClick,
}: PostRaceImmediateBannerProps) {
  const navigate = useNavigate();

  return (
    <section
      data-testid="post-race-immediate-banner"
      aria-label={t.plan.postRaceImmediateTitle}
      className="relative flex flex-col gap-3 rounded-lg border border-lime/40 bg-lime/10 p-4"
    >
      <button
        type="button"
        data-testid="post-race-immediate-dismiss"
        onClick={onDismiss}
        aria-label={t.plan.postRaceImmediateDismissAriaLabel}
        className="absolute right-2 top-2 text-bone-muted hover:text-bone"
      >
        ×
      </button>

      <header className="flex flex-col gap-1 pr-6">
        <h2 className="text-sm font-semibold text-lime">
          {t.plan.postRaceImmediateTitle}
        </h2>
        <p className="text-xs text-bone-muted">
          {t.plan.postRaceImmediateDescription}
        </p>
      </header>

      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          data-testid="post-race-immediate-empezar-plan"
          onClick={() => navigate("/plan/crear")}
          className="flex-1 rounded-md bg-lime px-3 py-2 text-sm font-medium text-graphite hover:bg-lime/90"
        >
          {t.plan.postRaceImmediateEmpezarPlan}
        </button>
        <button
          type="button"
          data-testid="post-race-immediate-mantenimiento"
          onClick={onMaintenanceClick}
          className="flex-1 rounded-md border border-border-subtle bg-carbon-raised px-3 py-2 text-sm text-bone-muted hover:border-border-strong"
        >
          {t.plan.postRaceImmediateMantenimiento}
        </button>
      </div>
    </section>
  );
}
